import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, IconButton, Typography, Stack, Divider,
  Paper, Grid
} from "@mui/material"; 
import { 
  Close, EditNote, Save, CheckCircle,
  ReportProblem
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { useParams } from "react-router-dom";

import { QK } from "../../../../../../base/qk";
import {
  createMentalCapacityAssessment,
  updateMentalCapacityAssessment,
  getConsentReasons,
  getDecisionMakers,
} from "../../../../../../api/services/Assessment/ConsentCapacity";

// কাস্টম সেকশন ইমপোর্ট
import {
  BestInterestsSection,
  ConsentSection,
  MentalCapacitySection,
} from "./ConsentCapacitySection";

const initialFormData = {
  details_of_care: "",
  able_to_consent: true,
  give_consent: true,
  consent_not_signed_method: "",
  consent_datetime: "",
  consent_reason: "",
  consent_provider: "",
  concerns_triggers: "",
  actions_taken: "",
  decision_urgent: false,
  brain_impairment: false,
  brain_impairment_types: [],
  can_understand: false,
  understand_evidence: "",
  can_retain: false,
  retain_evidence: "",
  can_weigh: false,
  weigh_evidence: "",
  can_communicate: false,
  communicate_evidence: "",
  assessment_impaired: false,
  impaired_evidence: "",
  has_capacity: false,
  additional_notes: "",
  imca_steps: "",
  relevant_circumstances: [],
  options_balance: "",
  outcome_reason: "",
  disagreement_record: "",
  restrictions_dol_needed: false,
  restrictions_dol: "",
  risks_identified_flag: false,
  risks_identified: "",
  decision_maker: "",
  signature: "",
};

const ConsentDialog = ({ open, handleClose, editingConsent }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { clientID } = useParams();
  const [formData, setFormData] = useState(initialFormData);

  // API Queries
  const { data: consentReasons, isLoading: loadingReasons } = useQuery(
    [QK.consentReasons],
    getConsentReasons,
    { enabled: open }
  );

  const { data: decisionMakers, isLoading: loadingMakers } = useQuery(
    [QK.decisionMakers],
    getDecisionMakers,
    { enabled: open && !formData.able_to_consent }
  );

  useEffect(() => {
    if (!open) {
      setFormData(initialFormData);
    } else if (editingConsent) {
      setFormData({
        ...initialFormData,
        ...editingConsent,
        give_consent: editingConsent.given_consent ?? true,
        relevant_circumstances: Array.isArray(editingConsent.relevant_circumstances)
          ? editingConsent.relevant_circumstances
          : JSON.parse(editingConsent.relevant_circumstances || "[]"),
        consent_reason: editingConsent.consent_reason ?? "",
        consent_datetime: editingConsent.consent_datetime ?? "",
        brain_impairment_types: editingConsent.brain_impairment_types || [],
        signature: editingConsent.signature || "",
      });
    }
  }, [open, editingConsent]);

  const mutation = useMutation(
    (payload) =>
      editingConsent
        ? updateMentalCapacityAssessment(editingConsent.id, payload)
        : createMentalCapacityAssessment(payload),
    {
      onSuccess: () => {
        enqueueSnackbar(`Assessment saved successfully!`, { variant: "success" });
        queryClient.invalidateQueries(QK.consents);
        handleClose();
      },
      onError: () => enqueueSnackbar("Failed to save record", { variant: "error" }),
    }
  );

  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = () => {
    const formPayload = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      if (key === "signature") {
        if (value instanceof File) formPayload.append(key, value);
      } else if (Array.isArray(value)) {
        formPayload.append(key, JSON.stringify(value));
      } else if (key === "consent_datetime") {
        formPayload.append(key, value ? new Date(value).toISOString() : new Date().toISOString());
      } else {
        formPayload.append(key, value);
      }
    });

    if (clientID) formPayload.append("client", clientID);
    mutation.mutate(formPayload);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      fullWidth 
      maxWidth="md"
      PaperProps={{ 
        sx: { 
          borderRadius: "28px", 
          bgcolor: "#fff", 
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.12)",
          backgroundImage: 'none'
        } 
      }}
    >
      <DialogTitle sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack direction="row" spacing={2.5} alignItems="center">
          <Box sx={{ p: 1.5, borderRadius: "14px", bgcolor: "#f1f5f9", color: "#0f172a", display: 'flex' }}>
            <EditNote />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900, color: "#0f172a", lineHeight: 1.2 }}>
              {editingConsent ? "Revise Capacity Record" : "Mental Capacity Assessment"}
            </Typography>
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
              Documentation under the Mental Capacity Act (MCA)
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={handleClose} sx={{ bgcolor: '#f8fafc', '&:hover': { bgcolor: '#f1f5f9' } }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ borderStyle: 'dashed', opacity: 0.6 }} />

      <DialogContent sx={{ p: 4 }}>
        <Stack spacing={4}>
          
          <ConsentSection
            formData={formData}
            handleChange={handleChange}
            loadingReasons={loadingReasons}
            consentReasons={consentReasons}
            isEditing={!!editingConsent}
          />

          {!formData.able_to_consent && (
            <Stack spacing={4}>
              <Divider sx={{ borderStyle: 'dashed' }} />
              
              <MentalCapacitySection
                formData={formData}
                handleChange={handleChange}
              />

              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  bgcolor: "#f8fafc", 
                  borderRadius: "24px", 
                  border: "1px solid #e2e8f0",
                  background: "linear-gradient(145deg, #f8fafc 0%, #ffffff 100%)"
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 800, color: "#475569", mb: 3, textAlign: 'center', px: 2 }}>
                  Based on the assessment above, does the individual have capacity?
                  <br/>
                  <Typography component="span" variant="caption" sx={{ color: "#94a3b8", fontWeight: 600 }}>
                    Selecting 'No' will enable Best Interests Assessment.
                  </Typography>
                </Typography>

                <Grid container spacing={2}>
                  {[
                    { value: true, label: "Yes, Has Capacity", sub: "Individual can consent", icon: <CheckCircle />, color: "#10b981" },
                    { value: false, label: "No, Lacks Capacity", sub: "Requires Best Interests", icon: <ReportProblem />, color: "#ef4444" }
                  ].map((option) => {
                    const isSelected = formData.has_capacity === option.value;
                    return (
                      <Grid size={{ xs: 12, sm: 6 }} key={option.label}>
                        <Box
                          onClick={() => handleChange("has_capacity", option.value)}
                          sx={{
                            cursor: 'pointer',
                            p: 2.5,
                            borderRadius: "18px",
                            border: "2px solid",
                            borderColor: isSelected ? option.color : "#e2e8f0",
                            bgcolor: isSelected ? `${option.color}08` : "#fff",
                            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2.5,
                            "&:hover": {
                              transform: "translateY(-3px)",
                              boxShadow: isSelected ? `0 12px 20px -8px ${option.color}40` : "0 10px 15px -3px rgba(0,0,0,0.05)",
                              borderColor: isSelected ? option.color : "#cbd5e1"
                            }
                          }}
                        >
                          <Box sx={{ 
                            p: 1.5, 
                            borderRadius: "12px", 
                            bgcolor: isSelected ? option.color : "#f1f5f9", 
                            color: isSelected ? "#fff" : "#94a3b8",
                            display: 'flex',
                            transition: "0.2s"
                          }}>
                            {React.cloneElement(option.icon, { fontSize: 'small' })}
                          </Box>
                          <Box>
                            <Typography sx={{ fontWeight: 900, fontSize: "0.95rem", color: isSelected ? option.color : "#1e293b" }}>
                              {option.label}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                              {option.sub}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>

              {formData.has_capacity === false && (
                <Stack spacing={4}>
                  <Divider sx={{ borderStyle: 'dashed' }} />
                  <BestInterestsSection
                    formData={formData}
                    handleChange={handleChange}
                    decisionMakers={decisionMakers}
                    loadingMakers={loadingMakers}
                  />
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1, gap: 1.5, bgcolor: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
        <Button 
          onClick={handleClose} 
          sx={{ color: "#64748b", fontWeight: 800, textTransform: "none", px: 3 }}
        >
          Discard
        </Button>
        <Button 
          variant="contained" 
          disableElevation
          onClick={handleSubmit}
          startIcon={mutation.isLoading ? null : <Save />}
          sx={{ 
            bgcolor: "#0f172a", 
            borderRadius: "14px", 
            px: 5, py: 1.4, 
            fontWeight: 900,
            textTransform: "none",
            fontSize: '0.9rem',
            "&:hover": { bgcolor: "#1e293b", transform: 'scale(1.02)' },
            transition: 'all 0.2s'
          }}
        >
          {mutation.isLoading ? "Processing..." : editingConsent ? "Update Record" : "Finalize Entry"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConsentDialog;